import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCheck, Loader2, MailOpen } from "lucide-react";
import { GetNotificationsAPI } from "@/services/api/notification/getNotifications";
import {
  MarkAllNotificationsReadAPI,
  MarkNotificationReadAPI,
} from "@/services/api/notification/markNotificationRead";
import StudentNotificationRow, {
  type StudentNotification,
} from "@/components/student/StudentNotificationRow";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

const PAGE_SIZE = 20;

/**
 * Student notification centre — the web port of the RN `NotificationScreen`:
 * a paged list of notifications with an unread state, a "mark all read"
 * action in the header and a tap-through to the related screen.
 */
const StudentNotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<StudentNotification[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const sentinelRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(
    async (targetPage: number, mode: "initial" | "more" | "refresh") => {
      if (mode === "more") setLoadingMore(true);
      const resp = await GetNotificationsAPI(targetPage);
      if (mode === "more") setLoadingMore(false);
      setLoading(false);

      const result = resp?.result;
      if (resp?.status !== 0 || !Array.isArray(result)) {
        setError(true);
        setHasMore(false);
        return;
      }

      setError(false);
      setPage(targetPage);
      setHasMore(result.length >= PAGE_SIZE);
      setNotifications((prev) =>
        mode === "more" ? [...prev, ...result] : result,
      );
    },
    [],
  );

  const { refreshing, touchProps } = usePullToRefresh(() =>
    fetchPage(1, "refresh"),
  );

  useEffect(() => {
    fetchPage(1, "initial");
  }, [fetchPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const maybeLoadMore = () => {
      if (hasMore && !loadingMore && !loading) {
        fetchPage(page + 1, "more");
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      const onScroll = () => {
        const reachedBottom =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 400;
        if (reachedBottom) maybeLoadMore();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) maybeLoadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchPage, hasMore, loading, loadingMore, page]);

  const hasUnread = notifications.some(
    (item) => !item?.details?.is_read,
  );

  const handleMarkAllRead = async () => {
    if (markingAll) return;
    setMarkingAll(true);
    await MarkAllNotificationsReadAPI();
    setMarkingAll(false);
    setNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        details: { ...item?.details, is_read: true },
      })),
    );
  };

  const handlePressItem = (notification: StudentNotification) => {
    const details = notification?.details;
    if (!details) return;
    if (!details.is_read && details.id) {
      MarkNotificationReadAPI(details.id);
      setNotifications((prev) =>
        prev.map((item) =>
          item?.details?.id === details.id
            ? { ...item, details: { ...item.details, is_read: true } }
            : item,
        ),
      );
    }
    const target =
      details.entity_type === "activity" && details.entity_id
        ? `/student/activities/${details.entity_id}`
        : details.entity_type === "coupon"
          ? "/student/redeem"
          : "/student/home";
    navigate(target);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6" {...touchProps}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("Notifications")}
        </h1>
        <div className="flex items-center gap-2">
          {hasUnread && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={markingAll}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#ffa217] px-3 py-1.5 text-xs font-semibold text-[#ffa217] transition hover:bg-[#fff3e0] disabled:opacity-60"
            >
              {markingAll ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <CheckCheck size={14} />
              )}
              {t("Mark all as read")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <AdminListSkeleton count={4} />
      ) : error ? (
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="cloud-alert-outline"
            title={t("Something went wrong")}
            message={t("Please try again later.")}
            actionLabel={t("Retry")}
            onAction={() => fetchPage(1, "refresh")}
          />
        </div>
      ) : notifications.length === 0 ? (
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="Ionicons"
            iconName="notifications-off-outline"
            title={t("No notifications yet")}
            message={t(
              "Notifications from societies and Uninfo HK will appear here.",
            )}
            actionLabel={t("Refresh")}
            onAction={() => fetchPage(1, "refresh")}
          />
        </div>
      ) : (
        <>
          {refreshing && (
            <div className="mb-3 flex justify-center">
              <Loader2 size={18} className="animate-spin text-[#ffa217]" />
            </div>
          )}

          <div className="space-y-2.5">
            {notifications.map((notification, index) => (
              <StudentNotificationRow
                key={notification?.details?.id ?? `notification-${index}`}
                notification={notification}
                onPress={() => handlePressItem(notification)}
              />
            ))}
          </div>

          <div ref={sentinelRef} />

          {!hasUnread && !hasMore && (
            <div className="flex items-center justify-center gap-1.5 py-5 text-xs text-gray-400">
              <MailOpen size={14} />
              {t("You're all caught up")}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StudentNotificationsPage;
